import { useState } from "react";
import { useNavigate } from "react-router-dom";

// funcs
import { getAccruals } from "../../funcs/accruals/getAccruals";
import { getFormattedDate } from "../../funcs/helperFuncs";

import {
  DownloadAccrual,
  getAccrualTotals,
} from "../../funcs/accruals/downloadAccrual";

// hooks
import { useVerifyUnique } from "../../hooks/useVerifyUnique";
import { useEscape } from "../../hooks/useEscape";

import { messageAndErrorStore, projectStore, userStore } from "../../store/Store";

// styles and images
import "../project/Project.css";
import Close from "../../assets/close.svg";

import InlineInput from "../../formComponents/InlineInput";
import FormButton from "../../formComponents/FormButton";

export default function GenerateAccrual({ setTrigger }) {
  // global
  const currentUser = userStore((data) => data.currentUser);
  const setAuthStatus = userStore((data) => data.setAuthStatus);
  const JwtToken = userStore((data) => data.JwtToken);
  const currentProject = projectStore((data) => data.currentProject);
  const projectAccruals = projectStore((data) => data.projectAccruals);
  const setProjectAccruals = projectStore((data) => data.setProjectAccruals);
  const projectLoads = projectStore((data) => data.projectLoads);
  const projectAssetLoads = projectStore((data) => data.projectAssetLoads);
  const toggleNewMessage = messageAndErrorStore(data => data.toggleNewMessage)

  // local
  const [pending, setPending] = useState(false);
  const [accrual, setAccrual] = useState({
    project_id: currentProject.project.id,
    accrual_name: "",
    start_date: null,
    end_date: null,
    total_loads: 0,
    freight_weight_tons: 0,
    freight_weight_not_billed: 0,
    freight_weight_not_received: 0,
    sand_weight_tons: 0,
    sand_weight_not_billed: 0,
    sand_weight_not_received: 0,
    freight_rev: 0,
    freight_not_billed: 0,
    freight_cost: 0,
    freight_not_received: 0,
    sand_rev: 0,
    sand_not_billed: 0,
    sand_cost: 0,
    sand_not_received: 0,
    created_by_id: currentUser.id,
    updated_by_id: currentUser.id,
  });
  const navigate = useNavigate();
  const { exists } = useVerifyUnique(
    projectAccruals,
    "accrual_name",
    accrual.accrual_name,
  );

  const handleChange = (e) => {
    setAccrual({
      ...accrual,
      [e.target.name]: e.target.value,
    });
  };
  useEscape(setTrigger)
  const handleClose = () => {
    setTrigger(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPending(true);
    if (exists) {
      toggleNewMessage(
        "This accrual already exists for this project, please choose another name",
      );
      setPending(false);
      return;
    }
    const [accruedLoads, accruedAssetLoads, totals] = getAccruals(
      projectLoads,
      projectAssetLoads,
      accrual.start_date,
      accrual.end_date,
    );

    const start = getFormattedDate(accrual.start_date).split("T");
    const end = getFormattedDate(accrual.end_date).split("T");
    const { freightTotals, sandTotals, err } = await getAccrualTotals(
      start[0],
      end[0],
      currentProject.project.id,
      JwtToken,
      setAuthStatus,
      navigate,
    );
    
    if (err) {
      toggleNewMessage("Something went wrong")
      setPending(false)
      return
    }
    const uploadError = await DownloadAccrual(
      accruedLoads,
      accruedAssetLoads,
      accrual.accrual_name,
      currentProject.project.job_name,
      freightTotals,
      sandTotals,
    );

    if (uploadError === "") {
      toggleNewMessage(null, "Accrual Uploaded");
    } else {
      toggleNewMessage(uploadError);
    }

    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);
    const controller = new AbortController();

    const reqBody = accrual;
    reqBody.total_loads = totals.total_loads;
    reqBody.freight_weight_tons = totals.freight_weight_tons;
    reqBody.freight_weight_not_billed = totals.freight_weight_not_billed;
    reqBody.freight_weight_not_received = totals.freight_weight_not_received;
    reqBody.sand_weight_tons = totals.sand_weight_tons;
    reqBody.sand_weight_not_billed = totals.sand_weight_not_billed;
    reqBody.sand_weight_not_received = totals.sand_weight_not_received;
    reqBody.freight_rev = totals.freight_rev;
    reqBody.freight_not_billed = totals.freight_not_billed;
    reqBody.freight_cost = totals.freight_cost;
    reqBody.freight_not_received = totals.freight_not_received;
    reqBody.sand_rev = totals.sand_rev;
    reqBody.sand_not_billed = totals.sand_not_billed;
    reqBody.sand_cost = totals.sand_cost;
    reqBody.sand_not_received = totals.sand_not_received;
    reqBody.start_date = getFormattedDate(reqBody.start_date);
    reqBody.end_date = getFormattedDate(reqBody.end_date);
    const requestOptions = {
      method: "POST",
      body: JSON.stringify(reqBody),
      headers,
      signal: controller.signal,
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND}/pc/project-accrual/0`,
        requestOptions,
      );

      if (!response.ok) {
        if (response.status === 401) {
          setAuthStatus(false);
          navigate("/login");
          setTrigger(false);
          setPending(false);
        } else {
          toggleNewMessage("Something went wrong");
          setPending(false);
          return;
        }
      }

      const data = await response.json();
      if (data.error) {
        toggleNewMessage("Something went wrong");
        setPending(false);
        return;
      }
      setProjectAccruals(data);
      toggleNewMessage(null, "Accrual Added");
      setPending(false);
      setTrigger(false);
    } catch (error) {
      toggleNewMessage("Something went wrong");
      setPending(false);
    }
    return () => controller.abort();
  };

  return (
    <div className="popup">
      <div className="popup-inner">
        {!pending && (
          <div className="close">
            <img onClick={handleClose} src={Close} alt="close" />
          </div>
        )}
        <h3>Generate Accrual</h3>
        <form onSubmit={handleSubmit}>
          <InlineInput
            name={"accrual_name"}
            title={"Name"}
            type={"text"}
            onChange={handleChange}
            value={accrual.accrual_name}
            required={true}
          />
          <InlineInput
            name={"start_date"}
            title={"Start Date"}
            type={"date"}
            onChange={handleChange}
            required={true}
          />
          <InlineInput
            name={"end_date"}
            title={"End Date"}
            type={"date"}
            onChange={handleChange}
            required={true}
          />
          <div className="row g-3" style={{ padding: "20px" }}>
            <div className="col">
              <FormButton name={"Add Accrual"} pending={pending} />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
